import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Box,
  Chip,
  CircularProgress
} from '@mui/material';
import { 
  Save as SaveIcon, 
  Lock as LockIcon,
  Refresh as RefreshIcon 
} from '@mui/icons-material';
import { useSettings } from '../../contexts/SettingsContext';
import { apiClient } from '../../services/api';

interface BasicSettingsData {
  name: string;
  email: string;
  phone: string;
  address: {
    street: string;
    city: string;
    state: string;
    pincode: string;
    country: string;
  };
  businessHours: {
    monday: { open: string; close: string; closed: boolean };
    tuesday: { open: string; close: string; closed: boolean };
    wednesday: { open: string; close: string; closed: boolean };
    thursday: { open: string; close: string; closed: boolean };
    friday: { open: string; close: string; closed: boolean };
    saturday: { open: string; close: string; closed: boolean };
    sunday: { open: string; close: string; closed: boolean };
  };
  description: string;
}

interface BasicSettingsProps {
  salonId: string;
}

const BasicSettings: React.FC<BasicSettingsProps> = ({ salonId }) => {
  const [settings, setSettings] = useState<BasicSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const { checkAccess, getRestrictions } = useSettings();
  
  const canWrite = checkAccess('basic', 'write');
  const restrictions = getRestrictions('basic');

  useEffect(() => {
    fetchSettings();
  }, [salonId]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/settings/${salonId}/basic`);
      
      if (response.success) {
        const data = response.data.settings;
        setSettings({
          name: data.name || '',
          email: data.email || '',
          phone: data.phone || '',
          address: data.address || {
            street: '',
            city: '',
            state: '',
            pincode: '',
            country: 'India'
          },
          businessHours: data.businessHours || {
            monday: { open: '09:00', close: '18:00', closed: false },
            tuesday: { open: '09:00', close: '18:00', closed: false },
            wednesday: { open: '09:00', close: '18:00', closed: false },
            thursday: { open: '09:00', close: '18:00', closed: false },
            friday: { open: '09:00', close: '18:00', closed: false },
            saturday: { open: '09:00', close: '18:00', closed: false },
            sunday: { open: '10:00', close: '17:00', closed: false }
          },
          description: data.description || ''
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!canWrite || !settings) {
      setError('You do not have permission to modify these settings');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      const response = await apiClient.put(`/settings/${salonId}/basic`, settings);
      
      if (response.success) {
        setSuccess('Settings updated successfully');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSettings = (updates: Partial<BasicSettingsData>) => {
    if (settings) {
      setSettings({ ...settings, ...updates });
    }
  };

  const updateAddress = (addressUpdates: Partial<BasicSettingsData['address']>) => {
    if (settings) {
      setSettings({
        ...settings,
        address: { ...settings.address, ...addressUpdates }
      });
    }
  };

  const updateBusinessHours = (day: string, hours: { open: string; close: string; closed: boolean }) => {
    if (settings) {
      setSettings({
        ...settings,
        businessHours: {
          ...settings.businessHours,
          [day]: hours
        }
      });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!settings) {
    return (
      <Alert severity="error">
        Failed to load basic settings. Please try refreshing the page.
      </Alert>
    );
  }

  return (
    <Box sx={{ pr: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Basic Salon Settings</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {restrictions.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {restrictions.map(restriction => (
                <Chip
                  key={restriction}
                  icon={<LockIcon />}
                  label={restriction.replace(/_/g, ' ')}
                  color="warning"
                  size="small"
                />
              ))}
            </Box>
          )}
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchSettings}
            disabled={loading}
            size="small"
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Salon Name"
                    value={settings.name}
                    onChange={(e) => updateSettings({ name: e.target.value })}
                    disabled={!canWrite}
                    required
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={settings.email}
                    onChange={(e) => updateSettings({ email: e.target.value })}
                    disabled={!canWrite}
                    required
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={settings.phone}
                    onChange={(e) => updateSettings({ phone: e.target.value })}
                    disabled={!canWrite}
                    required
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Description"
                    value={settings.description}
                    onChange={(e) => updateSettings({ description: e.target.value })}
                    disabled={!canWrite}
                    placeholder="Brief description of your salon..."
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Address */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Address
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Street Address"
                    value={settings.address.street}
                    onChange={(e) => updateAddress({ street: e.target.value })}
                    disabled={!canWrite}
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="City"
                    value={settings.address.city}
                    onChange={(e) => updateAddress({ city: e.target.value })}
                    disabled={!canWrite}
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="State"
                    value={settings.address.state}
                    onChange={(e) => updateAddress({ state: e.target.value })}
                    disabled={!canWrite}
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Pincode"
                    value={settings.address.pincode}
                    onChange={(e) => updateAddress({ pincode: e.target.value })}
                    disabled={!canWrite}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Business Hours */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Business Hours
              </Typography>
              
              <Grid container spacing={2}>
                {Object.entries(settings.businessHours).map(([day, hours]) => (
                  <Grid item xs={12} md={6} key={day}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography sx={{ minWidth: 100, textTransform: 'capitalize' }}>
                        {day}:
                      </Typography>
                      <TextField
                        type="time"
                        label="Open"
                        value={hours.open}
                        onChange={(e) => updateBusinessHours(day, { ...hours, open: e.target.value })}
                        disabled={!canWrite || hours.closed}
                        size="small"
                        sx={{ width: 120 }}
                      />
                      <TextField
                        type="time"
                        label="Close"
                        value={hours.close}
                        onChange={(e) => updateBusinessHours(day, { ...hours, close: e.target.value })}
                        disabled={!canWrite || hours.closed}
                        size="small"
                        sx={{ width: 120 }}
                      />
                      <Button
                        variant={hours.closed ? "contained" : "outlined"}
                        color={hours.closed ? "error" : "primary"}
                        onClick={() => updateBusinessHours(day, { ...hours, closed: !hours.closed })}
                        disabled={!canWrite}
                        size="small"
                      >
                        {hours.closed ? 'Closed' : 'Open'}
                      </Button>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {canWrite && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            size="large"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default BasicSettings;